import React, { FC, useState, MouseEvent } from 'react'
/** @jsx jsx */
import { jsx, css } from '@emotion/core'
import Analytics from '@lib/Analytics'
import { validateQuestionInput } from '@lib/validators'
import { Question } from '@components/Question'
import { Button } from '@components/Button'
import {
  HeroSectionWrapper,
  InnerContent,
  BackgroundImage,
  FormWrapper,
  FormQuestionWrapper,
  Title,
  Text,
  BtnHelperText,
  ErrorText,
  getBtnStyles,
  SaveText,
  BottomText
} from './HeroSection.elements'
import {getPhoneNumber} from "@lib/PhoneNumber";
import Pixels from "@lib/Pixels";
import {LeadgenPopupType} from "@components/LeadgenPopupFlow";
import {compileAnalyticsEventProps} from "@lib/analytics-event-props";
import { LearnMoreBanner } from '@components/InfoBanner/LearnMoreBanner'

type Props = {
  addressZip: string
  buttonHelpText: string
  buttonText: string
  desktopImageUrl: string
  inputs?: QuestionDataInput[]
  mobileImageUrl: string
  text: string
  title: string
  onSubmit: (value: string) => Promise<boolean>
  saveText?: string
  bottomText?: string
  textWidth?: string
  titleWidth?: string
  useCallButton?: boolean
  phoneMapping?: PhoneMapping
  onBottomTextClick?: (infoBlockActionType: LeadgenPopupType) => void
  centered?: boolean
  useQualifyButton?: boolean
}

export const HeroSection: FC<Props> = ({
  addressZip,
  buttonHelpText,
  buttonText,
  desktopImageUrl,
  inputs,
  mobileImageUrl,
  onSubmit,
  text,
  title,
  saveText,
  bottomText,
  textWidth,
  titleWidth,
  useCallButton,
  phoneMapping,
  onBottomTextClick,
  centered,
  useQualifyButton
}) => {
  const [value, setValue] = useState<string>(addressZip || '')
  const [error, setError] = useState<{
    key: string
    text: string
    answer: string
  }>()
  const [showError, setShowError] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const input = inputs && inputs.length > 0 ? inputs[0] : false;
  const phoneNumber = getPhoneNumber(phoneMapping, 'pretty')

  const handleChange = (_name: string, value: string) => {
    setValue(value)
    setError(undefined)
  }

  const buttonLabel = useCallButton ? `Call ${phoneNumber}` : buttonText;

  const handleSubmit = async (event: MouseEvent<HTMLButtonElement>) => {

    event.preventDefault()
    const error = validateQuestionInput(input, value)
    setError(error || undefined)

    if (!value || !!error) {
      Analytics.log('Form Error', { location: 'hero-section', info: 'invalid zip' })
      setShowError(true)
    } else {
      Analytics.log('Click', { location: 'hero-section', type: 'quote' })
      setShowError(false)
      setLoading(true)
      const response = await onSubmit(value)
      if(!response){
        setLoading(false)
      }
    }
  }

  const handleBottomTextClick = () => {
    //for now it opens popup leads
    return onBottomTextClick && onBottomTextClick(LeadgenPopupType.AUTO_INSURANCE);
  }

  const handleCallNow = () => {

    Pixels.callEvent()
    Analytics.log('Call now', {
      location: 'info-block', info: `tel:${ phoneNumber }`
    })

    window.location.href = `tel:${ phoneNumber }`
  }

  const handleQualify = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    Analytics.log('Click', compileAnalyticsEventProps({ location: 'hero-button' }))

    setTimeout(() => {
      window.location.href = '/signup'
    }, 500)
  }

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (useCallButton){
      return handleCallNow()
    }
    if (useQualifyButton){
      return handleQualify(event)
    }
    return handleSubmit(event)
  }

  const hasError = showError && !!error

  return (
    <HeroSectionWrapper centered={centered}>

      <BackgroundImage
        mobileSrc={mobileImageUrl}
        desktopSrc={desktopImageUrl}
      />

      <InnerContent centered={centered}>
        <Title titleWidth={titleWidth} dangerouslySetInnerHTML={{__html: title}} />
        <Text textWidth={textWidth} dangerouslySetInnerHTML={{__html: text}} />
        <FormWrapper>
          { input &&
            <FormQuestionWrapper>
              <Question
                value={value}
                error={hasError}
                inputData={input}
                onAnswerQuestion={handleChange}
                id="hero-question"
              />
              {hasError && error && (
                <ErrorText>{value ? error.text : 'Please enter a value.'}</ErrorText>
              )}
            </FormQuestionWrapper>
          }
          { saveText && <SaveText dangerouslySetInnerHTML={{__html: saveText}}/>  }
          <Button
            onClick={handleClick}
            variant="green"
            css={getBtnStyles(hasError)}
            type="submit"
            disabled={loading}
          >
            {loading ? 'Loading ...' : buttonLabel}
          </Button>

          <BtnHelperText>{buttonHelpText}</BtnHelperText>
        </FormWrapper>
        { bottomText && <BottomText onClick={handleBottomTextClick} dangerouslySetInnerHTML={{__html: bottomText}}/>  }
      </InnerContent>
    </HeroSectionWrapper>
  )
}
